import {
  type ComponentType,
  createElement,
  type LazyExoticComponent,
  Suspense,
} from 'react';
import { createRoot } from 'react-dom/client';

import { AppLayout, ForceLocationPickerDialog, PageTabs } from '@/components';
import { LoadingFallback } from '@/components/LoadingFallback';
import { TooltipProvider } from '@/components/ui/tooltip';

import { applyStoredAdminTheme } from '@/lib/adminTheme';
import {
  filterSectionsByUiVersion,
  readAdminUiVersion,
} from '@/lib/adminUiVersion';

import { initI18n, transformLocales } from '@/helpers';

import { getAdminThemeStorageKey } from '@/constants/localStorage';

import { InternalNavigationData } from '@/types/ui';

import {
  ConfirmationProvider,
  QueryProvider,
  SidebarProvider,
  ThemeProvider,
} from '@/providers';

import * as localization from '@/localization';

import * as LazyPages from '@/pages/admin/lazy';

export function renderAdminLayout(
  pageContainer = document.getElementById('page-content'),
  headerContainer = document.getElementById('page-header'),
  renderContainer = document.getElementById('page-root'),
  internalNavigation = document.getElementById('internal-navigation'),
) {
  const { component_name, ...pageProps } = JSON.parse(
    pageContainer?.getAttribute('data') || '{}',
  );

  const storedParams = JSON.parse(
    headerContainer?.getAttribute('data') || '{}',
  );
  const headerParams = {
    ...storedParams,
    sections: storedParams.is_system_owner
      ? filterSectionsByUiVersion(
          storedParams.sections ?? [],
          readAdminUiVersion(),
        )
      : (storedParams.sections ?? []),
  };
  const locales = transformLocales(
    localization.common,
    localization.header,
    localization.validation,
    localization.jodit,
    localization[component_name as keyof typeof localization],
  );

  const { navigation } = internalNavigation
    ? (JSON.parse(
        internalNavigation.getAttribute('data')!,
      ) as InternalNavigationData)
    : { navigation: [] };

  initI18n(locales);
  applyStoredAdminTheme(headerParams.user_name);

  const { is_location_selection_required } = headerParams;

  const PageComponent = (
    LazyPages as unknown as Record<
      string,
      LazyExoticComponent<ComponentType<Record<string, unknown>>>
    >
  )[component_name];

  if (!PageComponent) {
    console.error(`Page component "${component_name}" not found in lazy pages`);
    return;
  }

  const hasNavigation = navigation?.length > 0;

  createRoot(renderContainer!).render(
    <QueryProvider>
      <SidebarProvider className="flex-col">
        <ConfirmationProvider>
          <TooltipProvider>
            <ThemeProvider
              storageKey={getAdminThemeStorageKey(headerParams.user_name)}
            >
              <AppLayout {...headerParams}>
                <Suspense fallback={<LoadingFallback />}>
                  {hasNavigation && <PageTabs tabs={navigation} />}
                  {createElement(PageComponent, pageProps)}
                </Suspense>
                {is_location_selection_required && (
                  <ForceLocationPickerDialog
                    locations={headerParams.location_props.locations}
                    apiUrl={headerParams.location_props.url}
                  />
                )}
              </AppLayout>
            </ThemeProvider>
          </TooltipProvider>
        </ConfirmationProvider>
      </SidebarProvider>
    </QueryProvider>,
  );
}
